import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ActivityIndicator,
  Platform,
  TouchableOpacity,
} from 'react-native';
import { Mic, Play, Pause } from '../../../icons';

interface AudioRendererProps {
  fileUrl?: string;
  isLoading: boolean;
  hasError: boolean;
  fileName: string;
  isActive: boolean;
}

export const AudioRenderer: React.FC<AudioRendererProps> = ({
  fileUrl,
  isLoading,
  hasError,
  fileName,
  isActive,
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [position, setPosition] = useState(0); // ms
  const [duration, setDuration] = useState(0); // ms
  const [playerReady, setPlayerReady] = useState(false);
  const [playbackError, setPlaybackError] = useState(false);
  const listenerAdded = useRef(false);

  // Use refs to avoid stale closures in handlePlayPause
  const playerReadyRef = useRef(playerReady);
  const isPlayingRef = useRef(isPlaying);

  useEffect(() => {
    playerReadyRef.current = playerReady;
  }, [playerReady]);

  useEffect(() => {
    isPlayingRef.current = isPlaying;
  }, [isPlaying]);

  // Web: ref for HTML audio element
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Cleanup on unmount (native only)
  useEffect(() => {
    if (Platform.OS === 'web') return;
    return () => {
      try {
         
        const Sound = require('react-native-nitro-sound').default;
        Sound.stopPlayer();
        if (listenerAdded.current) {
          Sound.removePlayBackListener();
          listenerAdded.current = false;
        }
      } catch { /* ignore */ }
    };
  }, []);

  // Pause when carousel navigates away
  useEffect(() => {
    if (Platform.OS === 'web') {
      if (!isActive && audioRef.current) {
        audioRef.current.pause?.();
      }
      return;
    }
    if (!isActive && isPlaying) {
      try {
         
        const Sound = require('react-native-nitro-sound').default;
        Sound.pausePlayer();
      } catch { /* ignore */ }
      setIsPlaying(false);
    }
  }, [isActive, isPlaying]);

  const handlePlayPause = useCallback(async () => {
    if (!fileUrl) return;
    try {
       
      const Sound = require('react-native-nitro-sound').default;
      if (!playerReadyRef.current) {
        if (!listenerAdded.current) {
          Sound.addPlayBackListener((e: { currentPosition?: number; duration: number }) => {
            setPosition(e.currentPosition ?? 0);
            if (e.duration > 0) setDuration(e.duration);
          });
          listenerAdded.current = true;
        }
        await Sound.startPlayer(fileUrl);
        setPlayerReady(true);
        setIsPlaying(true);
      } else if (isPlayingRef.current) {
        await Sound.pausePlayer();
        setIsPlaying(false);
      } else {
        await Sound.resumePlayer();
        setIsPlaying(true);
      }
    } catch {
      setPlaybackError(true);
    }
  }, [fileUrl]);

  if (hasError || playbackError) {
    return (
      <View style={styles.center}>
        <Text style={styles.errorText}>
          {playbackError ? 'Audio playback failed' : 'Failed to load audio'}
        </Text>
        <Text style={styles.errorSub}>{fileName}</Text>
      </View>
    );
  }

  if (isLoading || !fileUrl) {
    return (
      <View style={styles.center}>
        <ActivityIndicator size="large" color="#fff" />
      </View>
    );
  }

  // Web: native HTML5 audio element
  if (Platform.OS === 'web') {
    return (
      <View style={styles.container}>
        <Mic size={64} color="rgba(255,255,255,0.7)" />
        <Text style={styles.fileName} numberOfLines={2}>{fileName}</Text>
        <View style={styles.webAudioContainer}>
          {React.createElement('audio', {
            ref: audioRef,
            src: fileUrl,
            controls: true,
            autoPlay: false,
            style: { width: '100%', outlineStyle: 'none', outlineWidth: 0 },
          })}
        </View>
      </View>
    );
  }

  // Native: react-native-nitro-sound with play/pause/progress UI
  const formatTime = (ms: number) => {
    const totalSecs = Math.floor(ms / 1000);
    const m = Math.floor(totalSecs / 60);
    return `${m}:${String(totalSecs % 60).padStart(2, '0')}`;
  };

  const progress = duration > 0 ? Math.min(position / duration, 1) : 0;

  return (
    <View style={styles.container}>
      <Mic size={64} color="rgba(255,255,255,0.7)" />
      <Text style={styles.fileName} numberOfLines={2}>{fileName}</Text>

      <TouchableOpacity
        style={styles.playButton}
        onPress={handlePlayPause}
        accessibilityLabel={isPlaying ? 'Pause audio' : 'Play audio'}
        accessibilityRole="button"
      >
        {isPlaying ? <Pause size={40} color="#fff" /> : <Play size={40} color="#fff" />}
      </TouchableOpacity>

      <View style={styles.progressRow}>
        <Text style={styles.timeText}>{formatTime(position)}</Text>
        <View style={styles.progressTrack}>
          <View style={[styles.progressFill, { flex: progress }]} />
          <View style={{ flex: 1 - progress }} />
        </View>
        <Text style={styles.timeText}>{formatTime(duration)}</Text>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#000',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  fileName: {
    color: 'rgba(255,255,255,0.85)',
    fontSize: 15,
    marginTop: 16,
    textAlign: 'center',
  },
  playButton: {
    marginTop: 28,
    backgroundColor: 'rgba(255,255,255,0.15)',
    borderRadius: 40,
    padding: 16,
  },
  progressRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 20,
    width: '100%',
    paddingHorizontal: 8,
    gap: 8,
  },
  progressTrack: {
    flex: 1,
    flexDirection: 'row',
    height: 4,
    backgroundColor: 'rgba(255,255,255,0.25)',
    borderRadius: 2,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    backgroundColor: '#fff',
    borderRadius: 2,
  },
  timeText: {
    color: 'rgba(255,255,255,0.6)',
    fontSize: 12,
    minWidth: 36,
    textAlign: 'center',
  },
  center: {
    flex: 1,
    backgroundColor: '#000',
    alignItems: 'center',
    justifyContent: 'center',
  },
  errorText: {
    color: '#fff',
    fontSize: 16,
    marginBottom: 4,
  },
  errorSub: {
    color: 'rgba(255,255,255,0.6)',
    fontSize: 13,
  },
  webAudioContainer: {
    width: '80%',
    marginTop: 24,
  },
});
